// INCLUDE THESE PAGES IN THE MAIN BUNDLE
// LAZY-LOAD OTHER PAGES USING import()
// ============================================
import nprogress from 'nprogress'
import Vue from 'vue'
import VueRouter from 'vue-router'

import PageLogin from '@cv/pages/PageLogin.vue'
// import PageSplash from '@cv/pages/PageSplash.vue'
import store from '@ss/store'

nprogress.configure({
	showSpinner: false,
	minimum: 0.25,
	speed: 500,
	ease: 'ease'
})

Vue.use(VueRouter)

// Create a map to store scroll positions
const scrollPositions = new Map<string, { x: number; y: number }>()

const router = new VueRouter({
	scrollBehavior(to, from, storedPosition) {
		const scrollableDiv = document.querySelector('.app-content')
		if (!scrollableDiv) return

		// Store current scroll position before leaving
		if (from.path) {
			scrollPositions.set(from.path, {
				y: scrollableDiv.scrollTop,
				x: scrollableDiv.scrollLeft
			})
		}

		// On forward navigation (no stored position), scroll to top
		if (storedPosition) {
			// On back navigation, restore position
			const storedPosition = scrollPositions.get(to.path)
			if (storedPosition) {
				setTimeout(() => {
					scrollableDiv.scrollTo({
						top: storedPosition.y,
						left: storedPosition.x,
						behavior: 'instant'
					})
				})
			}
		} else {
			setTimeout(() => {
				scrollableDiv.scrollTo({
					top: 0,
					behavior: 'instant'
				})
			})
		}
	},
	mode: 'history',
	routes: [
		{
			name: 'login',
			alias: '/',
			path: '/manage',
			component: PageLogin,
			meta: {
				requireAuth: false,
				requireAccount: false,
				title: 'Login'
			}
		},
		{
			name: 'access',
			path: '/access',
			component: () => import('@cv/pages/PageAuthWithSubscriber.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false
			}
		},
		{
			name: 'register',
			path: '/register',
			component: () => import('@cv/pages/PageRegister.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false,
				title: 'Register'
			}
		},
		{
			name: 'recover-password',
			path: '/recover-password',
			component: () => import('@cv/pages/PageRecoverPassword.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false,
				title: 'Recover Password'
			}
		},
		{
			name: 'reset-password',
			path: '/reset-password/:email/:token',
			component: () => import('@cv/pages/PageResetPassword.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false,
				disableRouteBasedOnAuthStatus: true,
				title: 'Reset Password'
			}
		},
		{
			name: 'accept-collaboration',
			path: '/accept/:collaborationId',
			component: () => import('@cv/pages/PageAcceptCollaboration.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false
			}
		},
		{
			name: 'home',
			path: '/home',
			component: () => import('@ss/pages/PageHome.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				tour: {
					name: 'app',
					target: '#tour-watch-tour'
				}
			}
		},

		{
			name: 'posts',
			path: '/posts',
			component: () => import('@ss/pages/PagePostPerformance.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},

		{
			name: 'platforms',
			path: '/platforms',
			component: () => import('@ss/pages/PagePlatformPerformance.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'watched-accounts',
			path: '/watched-accounts',
			component: () => import('@ss/pages/PageWatchedAccounts.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'watched-account',
			path: '/watched-accounts/:accountId/:section?',
			component: () => import('@ss/pages/PageWatchedAccount.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'app-config',
			path: '/app-config',
			component: () => import('@cv/pages/PageAppConfig.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'app-queue',
			path: '/app-queue',
			component: () => import('@cv/pages/PageAppQueue.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'errors',
			path: '/errors',
			component: () => import('@cv/pages/PageErrors.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'notifications',
			path: '/notifications',
			component: () => import('@cv/pages/PageNotifications.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'team',
			path: '/team',
			component: () => import('@cv/pages/PageTeam.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Team'
			}
		},
		{
			name: 'billing',
			path: '/billing',
			component: () => import('@cv/pages/PageBilling.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Billing'
			}
		},
		{
			name: 'user',
			path: '/me',
			component: () => import('@cv/pages/PageUserSettings.vue'),
			meta: {
				requireAuth: true,
				requireAccount: false,
				showMenu: 'account',
				title: 'User settings'
			}
		},
		{
			name: 'user-settings',
			path: '/users/:userId',
			component: () => import('@cv/pages/PageUserSettings.vue'),
			meta: {
				requireAuth: true,
				requireAccount: false,
				showMenu: 'account',
				title: 'User settings'
			}
		},
		{
			name: 'users',
			path: '/users',
			component: () => import('@cv/pages/PageUsers.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Users'
			}
		},
		{
			name: 'dealers',
			path: '/dealers',
			component: () => import('@ss/pages/PageDealers.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Dealers'
			}
		},
		{
			name: 'account-settings',
			path: '/account/settings',
			component: () => import('@ss/pages/PageDealerSettings.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Account Settings'
			}
		},
		{
			name: 'media',
			path: '/media',
			component: () => import('@cv/pages/PageMedia.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Media'
			}
		},
		{
			name: 'social-entities',
			path: '/social-entities',
			component: () => import('@cv/pages/PageSocialEntities.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Social Channels'
			}
		},
		{
			name: 'dealer',
			path: '/dealers/:dealerId/:section?',
			component: () => import('@ss/pages/PageDealer.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: route => store.getters['account/byId'](route.params.dealerId).name
			}
		}
	]
})

router.beforeEach(async (to, from, next) => {
	store.commit('app/SET_ROUTE_LOADING', 'true')
	nprogress.start()
	try {
		store.commit('app/SET_FROM_ROUTE', from)
		if (to.meta.requireAuth) {
			if (to.query && to.query.u) {
				// this is a share link with an auth code
				await store.dispatch('user/loginWithAuthCode', to.query.u)
				return next({ name: 'login', query: { redirect: to.path } })
			}
			try {
				await store.dispatch('user/authenticate')
				if (to.meta.requireAccount && !(await store.dispatch('account/hasAny'))) {
					console.log(to.path, 'Route access denied. Account required.')
					//   if(to.name !== 'select-plan') {
					//     console.log('redirecting to select-plan');
					//     return next({name: 'select-plan'});
					//   }
					throw new Error('Account required')
				}
				return next()
			} catch (e) {
				console.log(
					to.path,
					'Route access denied. Missing authentication OR missing required account.'
				)
				return next({ name: 'login', query: { redirect: to.path } })
			}
		} else if (to.meta.requireSubscriber && !store.getters['subscriber/isSubscribed']) {
			const requireSubscriberRoute = {
				name: 'access',
				query: {
					redirect: to.path
				} as Record<string, string>
			}
			if (typeof to.meta.title === 'function') {
				requireSubscriberRoute.query.resource = to.meta.title(to)
			} else if (typeof to.meta.title === 'string') {
				requireSubscriberRoute.query.resource = to.meta.title
			}
			return next(requireSubscriberRoute)
		}
		return next()
	} catch (e) {
		console.error('router.beforeEach error', e)
		next('/')
	}
})

router.afterEach(() => {
	nprogress.done(true)
	store.commit('app/SET_ROUTE_LOADING', false)
})

router.onError(error => {
	console.error('Router error', error)
	store.commit('app/SET_ROUTE_LOADING', false)
	nprogress.done()
})

export default router
